import { sampleQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient, type DownloadedFile } from '@/lib/api-client';
import type {
  OrgSampleFilters,
  ProteoformSampleSummaryFilters,
  ProteoformSampleSummarySortField,
  SampleFilters,
  SampleProteoformFilters,
  SampleQcSummary,
  SampleSortField,
  SampleWithNamesPublic,
  PaginatedSamplesResponse,
  SampleSearchRequest,
  SortOrder,
} from '@/types';

/** A malformed response repeats identically on every attempt, so it must not burn the backoff. */
export const CONTRACT_BREACH = { status: 502, code: 'CONTRACT_BREACH' } as const;

export async function getSample(orgId: string, sampleId: string): Promise<SampleWithNamesPublic> {
  const result = await apiClient.get<SampleWithNamesPublic>(sampleQueryKeys.endpoints.detail(orgId, sampleId), {
    expectJson: true,
  });

  // With expectJson the only route to null is a 204, which on a detail GET is a backend contract
  // breach, not a missing sample — 502 keeps it terminal without reporting the sample as absent.
  if (result === null) {
    console.error('[getSample] detail endpoint returned an empty body', { orgId, sampleId });
    throw Object.assign(new Error(`Sample ${sampleId}: endpoint returned an empty body`), CONTRACT_BREACH);
  }

  // The response type is asserted, not generated (see the route loader) — without this a shape
  // change ships as a silently half-empty detail page instead of a visible failure.
  if (typeof (result as { id?: unknown }).id !== 'string') {
    console.error('[getSample] unexpected detail response shape', { orgId, sampleId });
    throw Object.assign(new Error(`Sample ${sampleId}: unexpected detail response shape`), CONTRACT_BREACH);
  }

  return result;
}

export async function searchStudySamples(
  orgId: string,
  studyId: string,
  request: SampleSearchRequest,
): Promise<PaginatedSamplesResponse> {
  const result = await apiClient.post<PaginatedSamplesResponse>(
    studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
    request,
    { expectJson: true },
  );
  if (result === null) throw new Error('searchStudySamples: endpoint returned no content');
  return result;
}

// Org-wide QC tallies, deliberately independent of the samples grid's filter state — the
// backend counts every sample in scope, not the current page.
export async function fetchSamplesQcSummary(orgId: string): Promise<SampleQcSummary> {
  const result = await apiClient.get<SampleQcSummary>(sampleQueryKeys.endpoints.qcSummary(orgId), {
    expectJson: true,
  });

  if (result === null) throw new Error('fetchSamplesQcSummary: endpoint returned no content');
  return result;
}

export interface StudySamplesDownloadOptions {
  sortBy?: SampleSortField;
  sortOrder?: SortOrder;
  filters?: SampleFilters | null;
}

/** Streams a study's full sample list as CSV. */
export async function downloadStudySamplesCsv(
  orgId: string,
  studyId: string,
  options: StudySamplesDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(studyQueryKeys.endpoints.samplesDownload(orgId, studyId), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      // The endpoint takes `filters` as a JSON-encoded query param, not a body.
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}

export interface OrgSamplesDownloadOptions {
  sortBy?: SampleSortField;
  sortOrder?: SortOrder;
  filters?: OrgSampleFilters | null;
}

/** Streams the org's full (unbounded) sample list as CSV, honouring the grid's sort and filters. */
export async function downloadOrgSamplesCsv(
  orgId: string,
  options: OrgSamplesDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(sampleQueryKeys.endpoints.download(orgId), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      // The endpoint takes `filters` as a JSON-encoded query param, not a body.
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}

/** Streams one sample's full detail as a one-row CSV — the Sample Details page's own download. */
export async function downloadOrgSampleCsv(orgId: string, sampleId: string): Promise<DownloadedFile> {
  return apiClient.download(sampleQueryKeys.endpoints.detailDownload(orgId, sampleId));
}

export interface SampleProteoformsDownloadOptions {
  // A ProteoformSortField value or any epitope column key — see ProteoformSortField's own doc
  // comment in @/types for why this is a plain string, not the closed type.
  sortBy?: string;
  sortOrder?: SortOrder;
  thresholdCpm?: number;
  filters?: SampleProteoformFilters | null;
}

/** Streams one sample's proteoform rows as CSV, honouring the same threshold/sort/filters used to fetch the on-screen table. */
export async function downloadSampleProteoformsCsv(
  orgId: string,
  sampleId: string,
  options: SampleProteoformsDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder, thresholdCpm } = options;

  return apiClient.download(sampleQueryKeys.endpoints.proteoformsDownload(orgId, sampleId), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      threshold_cpm: thresholdCpm,
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}

export interface StudyProteoformsDownloadOptions extends SampleProteoformsDownloadOptions {
  /** Required: the study's results tables are per-assay, so the scope is meaningless without it. */
  assayId: string;
}

/** Streams a study's proteoform rows for one assay as CSV, honouring the on-screen threshold/sort/filters. */
export async function downloadStudyProteoformsCsv(
  orgId: string,
  studyId: string,
  options: StudyProteoformsDownloadOptions,
): Promise<DownloadedFile> {
  const { assayId, filters, sortBy, sortOrder, thresholdCpm } = options;

  return apiClient.download(studyQueryKeys.endpoints.proteoformsDownload(orgId, studyId), {
    query: {
      assay_id: assayId,
      sort_by: sortBy,
      sort_order: sortOrder,
      threshold_cpm: thresholdCpm,
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}

export interface ProteoformSampleSummariesDownloadOptions {
  assayId: string;
  sortBy?: ProteoformSampleSummarySortField;
  sortOrder?: SortOrder;
  thresholdCpm?: number;
  filters?: ProteoformSampleSummaryFilters | null;
}

/** Streams a study's per-sample proteoform summary for one assay as CSV. */
export async function downloadProteoformSampleSummariesCsv(
  orgId: string,
  studyId: string,
  options: ProteoformSampleSummariesDownloadOptions,
): Promise<DownloadedFile> {
  const { assayId, filters, sortBy, sortOrder, thresholdCpm } = options;

  return apiClient.download(studyQueryKeys.endpoints.proteoformSampleSummariesDownload(orgId, studyId), {
    query: {
      assay_id: assayId,
      sort_by: sortBy,
      sort_order: sortOrder,
      threshold_cpm: thresholdCpm,
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}
